import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
  Button,
  Modal,
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Divider,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import styles from './index.module.css'
import { useHistory, useParams } from 'react-router-dom';
import { createEstimationApi } from '../../services/estimation.js';
import toast, { Toaster } from 'react-hot-toast';
import ButtonLoader from '../ButtonLoader/index.js';
import { AuthContext } from '../../helpers/AuthContext';
import EmailComposer from '../EmailComposer';
import axios from 'axios';
import ClientContactsDropdown from '../ClientContactsDropdown';
import { toCents, fromCents, normalizePricing } from '../../utils/totals';
import { buildCustomerContactOptions } from './contactOptions';
import { transformData as transformPresentationData } from '../SelectedPresentation';
import { createCustomerContact, getCustomerDetails } from '../../services/customer';
import AddCustomerAddressModal from '../Estimation/AddressManager';
import CustomerContactDialog from '../CustomerContactDialog';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { listPresentationsByOrder, getProductComments } from '../../services/presentation';
import { deleteOrderItem } from '../../services/orderItem';

// helpers
function extractKey(units, key) {
  return units.map(unit => {
    return { [key]: unit[key] };
  });
}
const UNIT = 'unit';
const MARGIN = 'margin';
const AMOUNT = 'amount';

const pickNum = (...vals) => {
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const pickPositive = (...vals) => {
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

const mapToDataRows = (items, fallbackTitle = 'Item', unitOverride = null) => {
  return (items || []).map((item) => {
    return ({
      item: item?.CostName || item?.SubType || fallbackTitle,
      unit: item?.Quantity ?? 0,
      margin: item?.Margin || 0,
      tax: '',
      amount: fallbackTitle === 'Artwork' ? item.Charge * unitOverride : (item?.Retail * item?.Quantity) || 0,
    })
  });
};

const decorationToDataRows = (items, fallbackTitle = "Item") => {
  return (items || []).flatMap((item) =>
    (item?.Charge || []).map((charge) => ({
      item: charge?.Name || charge?.SubType || fallbackTitle,
      unit: charge?.Quantity ?? 0,
      margin: charge?.Margin || 0,
      tax: '',
      amount: (charge?.Retail * charge?.Quantity) || 0,
    }))
  );
}

export const transformData2 = (apiResponse) => {
  return apiResponse?.map((product, index) => {
    const taxRate = Number(product?.taxDetails?.rate) || 0;
    const pricingRows = Array.isArray(product?.pricings)
      ? product.pricings
      : (product?.pricings ? [product.pricings] : []);
    const dataRows = [];
    let totalUnits = 0;
    let subtotalC = 0;
    let taxC = 0;
    let marginC = 0;

    pricingRows.forEach((row) => {
      const normalizedRow = normalizePricing(row);
      const quantity = Number(normalizedRow?.qty || 0);
      const retailC = Number(normalizedRow?.retailC || 0);
      const costC = Number(normalizedRow?.costC || 0);
      const extC = retailC * quantity;

      totalUnits += quantity;
      subtotalC += extC;
      taxC += Math.round(extC * taxRate);
      marginC += (retailC - costC) * quantity;

      const quotedMargin = Number(row?.Margin ?? row?.margin);
      const displayMargin = Number.isFinite(quotedMargin)
        ? quotedMargin
        : Number(normalizedRow?.marginPct || 0);

      dataRows.push({
        item: 'Price Break',
        unit: quantity,
        margin: displayMargin,
        amount: fromCents(extC),
      });
    });

    (product?.decorations || []).forEach((decoration) => {
      (decoration?.Charge || []).forEach((charge) => {
        const quantity = Number(charge?.Quantity || 0);
        const netCost = pickNum(charge?.NetCost, charge?.netCost, charge?.Cost, charge?.cost);
        const retailBase = pickPositive(charge?.Retail, charge?.retail);
        const clientPrice = pickPositive(charge?.ClientPrice, charge?.clientPrice, charge?.Price, charge?.price);
        const retailRaw = (clientPrice > 0 && (retailBase <= 0 || Math.abs(retailBase - netCost) <= 0.01))
          ? clientPrice
          : (retailBase > 0 ? retailBase : clientPrice);
        const retailC = toCents(retailRaw);
        const costC = toCents(netCost);
        const extC = retailC * quantity;

        subtotalC += extC;
        taxC += Math.round(extC * taxRate);
        marginC += (retailC - costC) * quantity;

        const quotedMargin = Number(charge?.Margin ?? charge?.margin);
        const displayMargin = Number.isFinite(quotedMargin)
          ? quotedMargin
          : (retailC ? +(((retailC - costC) / retailC) * 100).toFixed(2) : 0);

        dataRows.push({
          item: charge?.Name || charge?.SubType || 'Decoration',
          unit: quantity,
          margin: displayMargin,
          amount: fromCents(extC),
        });
      });
    });

    dataRows.push({
      item: 'Total',
      unit: totalUnits,
      margin: subtotalC ? +((marginC / subtotalC) * 100).toFixed(2) : 0,
      amount: Number(fromCents(subtotalC + taxC)),
      taxAmount: Number(fromCents(taxC)),
      isTaxPresent: taxRate > 0,
    });

    return {
      id: product.ProductId,
      productId: product.ProductId,
      orderItemId: product.id || product.OrderItemId || product.orderItemId || null,
      lineId: product.lineId || product.LineId || `${product.ProductId}::${index}`,
      supplier: `${product?.SupplierName} ( ${product?.CurrencyCode || 'USD'} )`,
      name: product.Name,
      image: product.ImageUrl,
      description: product.Description,
      shortDescription: product.ShortDescription,
      data: dataRows,
      price: product.Subtotal,
      quantity: product?.Quantity,
      isTaxPresent: taxRate > 0,
    };
  }) || [];
};

export const buildCustomerAddressOption = (address = {}) => {
  const id = address?.id ?? address?.addressId ?? address?.customerAddressId ?? address?.AddressId ?? null;
  if (id === null || id === undefined || String(id).trim() === '') return null;

  const line2 = [address?.city, address?.state || address?.stateProvince].filter(Boolean).join(', ');
  const display = [
    address?.addressName,
    address?.street || address?.streetAddress,
    address?.suiteApt,
    line2,
    address?.postal || address?.postalZipCode,
    address?.country,
  ].filter(Boolean).join(' · ');

  return {
    id: String(id),
    label: address?.addressName || address?.street || address?.streetAddress || `Address ${id}`,
    display,
    raw: address,
  };
};

export const shouldShowSalesOrderBillingShipping = ({
  isSalesOrder = false,
  deliveryMode = 'draft',
} = {}) => isSalesOrder === true && deliveryMode === 'send';

export const getMissingSalesOrderCreationAddresses = ({
  billingContactId,
  billingName,
  billingAddressId,
  billingAddress,
  shippingContactId,
  shippingName,
  shippingAddressId,
  shippingAddress,
} = {}) => [
  String(billingContactId || billingName || '').trim() ? null : 'Billing Contact',
  String(billingAddressId || billingAddress || '').trim() ? null : 'Billing Address',
  String(shippingContactId || shippingName || '').trim() ? null : 'Shipping Contact',
  String(shippingAddressId || shippingAddress || '').trim() ? null : 'Shipping Address',
].filter(Boolean);

export const buildGeneratedDocumentProducts = ({ transformedData = [], sourceProducts = [], selectedProducts = [], orderId, orderTotal }) =>
  transformedData
    .filter((_, index) => selectedProducts.includes(index))
    .map((product) => {
      const sourceProduct = sourceProducts.find((item) =>
        String(item?.orderItemId ?? item?.OrderItemId ?? item?.id) === String(product?.orderItemId)
      );
      const totalRow = (product?.data || []).find((row) => row?._isTotal === true);
      const taxBreakdown = Array.isArray(totalRow?.taxBreakdown) ? totalRow.taxBreakdown : [];
      const taxableAmount = Number(totalRow?.taxableAmount ?? taxBreakdown?.[0]?.taxableAmount);
      const taxAmount = Number(totalRow?.taxAmount);
      const totalInclTax = Number(totalRow?.amount);
      const subtotal = Number(product?.subtotal ?? product?.price ?? sourceProduct?.subtotal ?? sourceProduct?.Subtotal);

      return {
        id: product.id,
        productId: product.productId || product.id,
        ProductId: sourceProduct?.ProductId ?? product.productId ?? product.id,
        // Copy catalogue metadata from the exact selected OrderItem. Do not
        // substitute another line that happens to use the same catalogue ID.
        Name: sourceProduct?.Name ?? sourceProduct?.name ?? product.name ?? '',
        Description: sourceProduct?.Description ?? sourceProduct?.description ?? product.description ?? '',
        ShortDescription: sourceProduct?.ShortDescription ?? sourceProduct?.shortDescription ?? product.shortDescription ?? '',
        ImageUrl: sourceProduct?.ImageUrl ?? sourceProduct?.imageUrl ?? product.image ?? '',
        SupplierName: sourceProduct?.SupplierName ?? sourceProduct?.supplierName ?? '',
        SupplierId: sourceProduct?.SupplierId ?? sourceProduct?.supplierId ?? null,
        CurrencyCode: sourceProduct?.CurrencyCode ?? sourceProduct?.currencyCode ?? 'CAD',
        Source: sourceProduct?.Source ?? 'asi',
        orderItemId: product.orderItemId,
        lineId: product.lineId,
        quantity: product.quantity,
        price: product.price,
        orderId,
        total: orderTotal,
        // Initial document creation must carry the selected cart line's
        // decoration charges and linked artwork just like regeneration does.
        // Keep selected-product financial stamps intact and never copy
        // decorations from another order item.
        decorations: Array.isArray(sourceProduct?.decorations)
          ? sourceProduct.decorations
          : (Array.isArray(sourceProduct?.Decorations) ? sourceProduct.Decorations : []),
        artworks: Array.isArray(sourceProduct?.artworks)
          ? sourceProduct.artworks
          : (Array.isArray(sourceProduct?.ArtWorks) ? sourceProduct.ArtWorks : []),
        ...(Number.isFinite(subtotal) ? { subtotal } : {}),
        ...(Number.isFinite(taxableAmount) ? { taxableAmount } : {}),
        ...(Number.isFinite(taxAmount) ? { taxAmount } : {}),
        ...(taxBreakdown.length ? { taxBreakdown } : {}),
        ...(Number.isFinite(totalInclTax) ? { totalInclTax } : {}),
      };
    });


// Updated component: now supports multi-recipient + opt-in email
//
const SelectedProductsPopup = ({
  open,
  setEstimationPopup,
  order,
  conversionStatus,
  clientContacts = [],
  fetchOrder,
  enforceAddressBeforeSalesOrderSend = false,
}) => {
  const { authState } = useContext(AuthContext);
  const transformedData = transformPresentationData(order.Products, order);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productFeedback, setProductFeedback] = useState({});
  const [deleteExcludedProducts, setDeleteExcludedProducts] = useState(true);
  const [loading, setLoading] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [deliveryMode, setDeliveryMode] = useState('draft');
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [billingContactId, setBillingContactId] = useState('');
  const [billingAddressId, setBillingAddressId] = useState('');
  const [shippingContactId, setShippingContactId] = useState('');
  const [shippingAddressId, setShippingAddressId] = useState('');
  const [addAddressOpen, setAddAddressOpen] = useState(false);
  const [addressTarget, setAddressTarget] = useState(null);
  const [contactDialog, setContactDialog] = useState({
    open: false,
    target: null,
    loading: false,
  });

  const isEstimate   = Number(conversionStatus) === 1;
  const isSalesOrder = Number(conversionStatus) === 2;
  const popupTitle   = isEstimate ? 'Create Estimate'
                    : isSalesOrder ? 'Create Sales Order'
                    : 'Convert Presentation';
  const ctaText      = isEstimate ? 'Create Estimate'
                    : isSalesOrder ? 'Create Sales Order'
                    : 'Convert';
  const [allContacts, setAllContacts] = useState([]);
  const [customerContactOptions, setCustomerContactOptions] = useState([]);
  const [selectedClientContact, setSelectedClientContact] = useState('');
  const [emailValue, setEmailValue] = useState({
    to: [],
    cc: [],
    bcc: [],
    subject: '',
    body: '',
    templateId: '',
    layout: 'plain',
    quickActions: {
      bccMe: false,
      bccOrderRep: false,
      bccClientRep: false,
      requestReadReceipt: false,
      attachPdf: false,
      attachDashboard: false,
    },
  });
  let { instanceUniqueId, orderId } = useParams();
  const history = useHistory();
  const primaryCustomerId = order?.attachedCustomers?.[0];
  const hasAttachedCustomerContacts =
    Array.isArray(customerContactOptions) &&
    customerContactOptions.length > 0 &&
    customerContactOptions[0]?.id !== '';
  const selectedBillingContact = customerContactOptions.find(
    (contact) => String(contact?.contactId || contact?.id || '') === String(billingContactId)
  ) || null;
  const selectedShippingContact = customerContactOptions.find(
    (contact) => String(contact?.contactId || contact?.id || '') === String(shippingContactId)
  ) || null;
  const selectedBillingAddress = customerAddresses.find((address) => String(address.id) === String(billingAddressId)) || null;
  const selectedShippingAddress = customerAddresses.find((address) => String(address.id) === String(shippingAddressId)) || null;
  const missingSalesOrderAddresses = getMissingSalesOrderCreationAddresses({
    billingContactId,
    billingAddressId,
    shippingContactId,
    shippingAddressId,
  });
  const showSalesOrderBillingShipping = shouldShowSalesOrderBillingShipping({
    isSalesOrder,
    deliveryMode,
  });
  const requireSalesOrderAddresses =
    showSalesOrderBillingShipping &&
    enforceAddressBeforeSalesOrderSend === true;
  const isCreateBlocked =
    !hasAttachedCustomerContacts ||
    !selectedClientContact ||
    (requireSalesOrderAddresses && missingSalesOrderAddresses.length > 0);

  const openCustomerPopup = () => {
    if (!primaryCustomerId) return;
    const url = `/instance/elite-promo-marketing/sales/customers?openCustomer=${primaryCustomerId}`;
    window.open(url, "_blank");
  };

  const handleClientContactSelect = (event, name) => {
    if (name === 'clientContact') {
      setSelectedClientContact(event.target.value);
    }
  };

  const handleClose = () => {
    setEstimationPopup(false)
  }

  const openAddAddress = (target) => {
    if (!primaryCustomerId) {
      toast.error('Attach a customer before adding an address.');
      return;
    }
    setAddressTarget(target);
    setAddAddressOpen(true);
  };

  const openAddContact = (target) => {
    if (!primaryCustomerId) {
      toast.error('Attach a customer before adding a contact.');
      return;
    }
    setContactDialog({ open: true, target, loading: false });
  };

  const closeAddContact = () => {
    if (contactDialog.loading) return;
    setContactDialog({ open: false, target: null, loading: false });
  };

  const handleContactCreated = async (payload) => {
    if (!primaryCustomerId) {
      toast.error('Attach a customer before adding a contact.');
      return;
    }

    try {
      setContactDialog((prev) => ({ ...prev, loading: true }));
      const response = await createCustomerContact(primaryCustomerId, payload);
      const createdContact = response?.data?.contact;

      if (!createdContact) {
        toast.error('Unable to create the customer contact.');
        setContactDialog((prev) => ({ ...prev, loading: false }));
        return;
      }

      const [option] = buildCustomerContactOptions([createdContact]);
      const optionId = option?.contactId || option?.id || '';

      if (!optionId) {
        toast.error('The new contact was created, but its id could not be resolved.');
        setContactDialog((prev) => ({ ...prev, loading: false }));
        return;
      }

      setCustomerContactOptions((prev) => {
        const map = new Map(
          (prev || []).map((contact) => [String(contact?.contactId || contact?.id || ''), contact])
        );
        map.set(String(optionId), option);
        return Array.from(map.values());
      });

      setAllContacts((prev) => {
        const map = new Map(
          (prev || []).map((contact) => [String(contact?.contactId || contact?.id || contact?.email || ''), contact])
        );
        map.set(String(optionId), option);
        return Array.from(map.values());
      });

      if (contactDialog.target === 'shipping') {
        setShippingContactId(String(optionId));
      } else {
        setBillingContactId(String(optionId));
      }

      toast.success('Customer contact created.');
      setContactDialog({ open: false, target: null, loading: false });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to create the customer contact.');
      setContactDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleAddressSaved = (savedAddress) => {
    const option = buildCustomerAddressOption(savedAddress);
    if (!option) {
      toast.error('Address was saved, but no address id was returned.');
      return;
    }

    setCustomerAddresses((prev) => {
      const next = [...prev];
      const existingIndex = next.findIndex((item) => String(item.id) === String(option.id));
      if (existingIndex >= 0) next[existingIndex] = option;
      else next.push(option);
      return next;
    });

    if (addressTarget === 'billing') setBillingAddressId(option.id);
    if (addressTarget === 'shipping') setShippingAddressId(option.id);
    setAddAddressOpen(false);
    setAddressTarget(null);
  };
  const handleCheckboxChange = (index) => {
    const updated = new Set(selectedProducts);
    if (updated.has(index)) updated.delete(index);
    else updated.add(index);
    setSelectedProducts([...updated]);
  };

  // turn chosen contact objects into email array
  const getContactValue = (contact) => contact?.contactId || contact?.id || '';
  const getContactEmail = (contact) => {
    const direct = String(contact?.email || '').trim();
    if (direct) return direct;
    const legacyId = String(contact?.id || '').trim();
    return legacyId.includes('@') ? legacyId : '';
  };
  const toEmailArray = (arr) => (arr || []).map(c => getContactEmail(c) || c?.id).filter(Boolean);

const handleConvertToEstimate = async () => {
  const sendEmailNow = deliveryMode === 'send';
  const normalizedConversionStatus =
    String(conversionStatus) === "2"
      ? "2"
      : "1";

  if (!hasAttachedCustomerContacts) {
    toast.error("Please attach a customer contact before creating this document.");
    return;
  }
  if (!selectedClientContact) {
    toast.error("Please select a client contact before creating this document.");
    return;
  }

  if (sendEmailNow && emailValue.to.length === 0) {
    toast.error("Choose at least one recipient in 'To'.");
    return;
  }

  if (requireSalesOrderAddresses && missingSalesOrderAddresses.length > 0) {
    toast.error(
      `Please add ${missingSalesOrderAddresses.join(' and ')} before sending this Sales Order.`
    );
    return;
  }

  try {
    const basePath = `/instance/${instanceUniqueId}/sales/orders/${orderId}`;
    const productsToSend = buildGeneratedDocumentProducts({
      transformedData,
      sourceProducts: order.Products,
      selectedProducts,
      orderId: order.OrderId,
      orderTotal: order?.Total?.amount,
    });

    if (productsToSend.length === 0) {
      toast.error("Select at least one product to convert.");
      return;
    }

    const selectedCustomerContact = customerContactOptions.find((c) => String(getContactValue(c)) === String(selectedClientContact));
    const resolvedClientEmail = getContactEmail(selectedCustomerContact) || selectedClientContact;
    const resolvedClientContactId = selectedCustomerContact?.contactId || null;

    const payload = {
      products: productsToSend,
      orderId: order?.OrderId,
      inhandsDate: order?.inHandsDate,
      customerId: order?.attachedCustomers?.[0],
      status: normalizedConversionStatus,
      clientContactEmail: resolvedClientEmail,
      clientContactId: resolvedClientContactId,
      billingContactId: selectedBillingContact?.contactId || selectedBillingContact?.id || null,
      billingName: selectedBillingContact?.name || '',
      billingAddressId: selectedBillingAddress?.id || null,
      billingAddress: selectedBillingAddress?.display || '',
      shippingContactId: selectedShippingContact?.contactId || selectedShippingContact?.id || null,
      shippingName: selectedShippingContact?.name || '',
      shippingAddressId: selectedShippingAddress?.id || null,
      shippingAddress: selectedShippingAddress?.display || '',
      sendEmailNow,
      ...(sendEmailNow
        ? {
            email: {
              to: toEmailArray(emailValue.to),
              cc: toEmailArray(emailValue.cc),
              bcc: toEmailArray(emailValue.bcc),
              subject: emailValue.subject,
              body: emailValue.body,
              templateId: emailValue.templateId || null,
              layout: emailValue.layout || 'plain',
              attachments: emailAttachments.map((file) => ({
                name: file.name,
                size: file.size,
                type: file.type,
              })),
            }
          }
        : {})
    };

    setLoading(true);

    const res = await createEstimationApi(payload);
    const createdDocument =
      res?.data?.salesOrder ||
      res?.data?.document ||
      res?.salesOrder ||
      res?.document ||
      res?.data ||
      res ||
      null;

    const createdDocumentId =
      createdDocument?.estimationId ||
      createdDocument?.id ||
      res?.data?.estimationId ||
      res?.data?.id ||
      res?.estimationId ||
      res?.id ||
      null;

    if (!createdDocumentId) {
      throw new Error('Failed to generate estimate/sales order.');
    }

    // Only remove excluded *persisted* cart lines once creation succeeded.
    // The existing OrderItem deletion endpoint blocks protected project items.
    const excludedItems = deleteExcludedProducts
      ? transformedData.filter((_, index) => !selectedProducts.includes(index))
      : [];
    const excludedIds = [...new Set(excludedItems.map((product) => {
      const source = order.Products.find((item) =>
        String(item?.orderItemId ?? item?.OrderItemId ?? item?.id) === String(product?.orderItemId)
      );
      return Number(source?.id ?? source?.orderItemId ?? source?.OrderItemId);
    }).filter((id) => Number.isSafeInteger(id) && id > 0))];
    let deleteFailures = 0;
    for (const id of excludedIds) {
      try {
        await deleteOrderItem(id);
      } catch (deleteError) {
        deleteFailures += 1;
        console.warn('Excluded cart item could not be deleted:', id, deleteError);
      }
    }
    if (deleteFailures > 0) {
      toast.error(`Document created, but ${deleteFailures} excluded cart item(s) could not be deleted. Please review the Order Cart.`);
    }

    const savedStatus = String(
      createdDocument?.status ??
      res?.data?.status ??
      res?.status ??
      (normalizedConversionStatus === "2" ? "2" : "1")
    );

    const targetTab = normalizedConversionStatus === "2" ? 'sales-order' : 'estimation';
    const targetUrl = `${basePath}/${targetTab}/${createdDocumentId}`;

    if (typeof fetchOrder === "function") {
      await fetchOrder({ suppressLoader: true });
    }

    setEstimationPopup(false);
    history.replace(targetUrl, {
      createdDocument: {
        id: String(createdDocumentId),
        estimationId: String(createdDocumentId),
        status: savedStatus,
        documentType: targetTab,
      },
    });
  } catch (error) {
    toast.error("Failed to convert. Please try again.");
  } finally {
    setLoading(false);
  }
};


  const parseAssignedTo = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string') {
      try { return JSON.parse(v); } catch { return []; }
    }
    return [];
  };

  const getAssignedEmail = (rep) => {
    const candidates = [rep?.email, rep?.Email, rep?.userEmail, rep?.id];
    return candidates.find((value) => typeof value === 'string' && value.includes('@')) || '';
  };

  const toContact = (email, rep) => {
    if (!email) return null;
    const name = `${rep?.first_name || ''} ${rep?.last_name || ''}`.trim();
    return {
      id: email,
      name: name || rep?.name || email,
    };
  };

  const assignedTo = useMemo(() => parseAssignedTo(order?.AssignedTo), [order?.AssignedTo]);
  const orderRepContacts = useMemo(
    () =>
      assignedTo
        .filter((rep) => String(rep?.role || '').toLowerCase() === 'order-rep')
        .map((rep) => toContact(getAssignedEmail(rep), rep))
        .filter(Boolean),
    [assignedTo]
  );
  const clientRepContacts = useMemo(
    () =>
      assignedTo
        .filter((rep) => String(rep?.role || '').toLowerCase() === 'client-rep')
        .map((rep) => toContact(getAssignedEmail(rep), rep))
        .filter(Boolean),
    [assignedTo]
  );

  useEffect(() => {
    let active = true;
    const loadPresentationFeedback = async () => {
      setProductFeedback({});
      if (!open || !order?.OrderId || !transformedData?.length) return;
      try {
        const presentations = await listPresentationsByOrder(order.OrderId);
        // Comments are tied to a presentation version and product catalogue ID.
        // Use the latest presentation, not comments from unrelated proposals.
        const latest = [...presentations].sort((a, b) =>
          new Date(b?.updatedAt || b?.createdAt || 0) - new Date(a?.updatedAt || a?.createdAt || 0)
        )[0];
        const presentationId = latest?.presentationId ?? latest?.id;
        if (!presentationId) return;
        const uniqueProductIds = [...new Set(transformedData
          .map((item) => item?.id ?? item?.productId)
          .filter((id) => id != null && String(id).trim() !== ''))];
        const feedback = {};
        await Promise.all(uniqueProductIds.map(async (productId) => {
          const result = await getProductComments(presentationId, productId);
          if (Array.isArray(result?.comments) && result.comments.length) {
            feedback[String(productId)] = result.comments;
          }
        }));
        if (active) setProductFeedback(feedback);
      } catch (error) {
        // Comments are supplemental and must never prevent conversion.
        console.warn('Unable to load presentation comments for conversion', error);
      }
    };
    loadPresentationFeedback();
    return () => { active = false; };
  }, [open, order?.OrderId, order?.Products?.length]);

  useEffect(() => {
    if (open && order?.Products?.length > 0) {
      setSelectedProducts(order.Products.map((_, i) => i));
    }
  }, [open, order?.OrderId, order?.Products?.length]);

  useEffect(() => {
    let mounted = true;

    const loadCustomerAddresses = async () => {
      if (!open || !isSalesOrder || !primaryCustomerId) {
        if (mounted) {
          setCustomerAddresses([]);
          setBillingContactId('');
          setBillingAddressId('');
          setShippingContactId('');
          setShippingAddressId('');
        }
        return;
      }

      try {
        const details = await getCustomerDetails(primaryCustomerId);
        if (!mounted) return;
        const addresses = (details?.customer?.address || [])
          .map(buildCustomerAddressOption)
          .filter(Boolean);
        setCustomerAddresses(addresses);
      } catch (error) {
        console.warn('Failed to load customer addresses for Sales Order creation', error);
        if (mounted) setCustomerAddresses([]);
      }
    };

    loadCustomerAddresses();
    return () => {
      mounted = false;
    };
  }, [open, isSalesOrder, primaryCustomerId]);

  // Build/sanitize contact list + defaults
  useEffect(() => {
    // initial selection: select all products

    // Sanitize attached customer contacts for the Client Contact selector.
    const normalizedCustomerContacts = buildCustomerContactOptions(clientContacts);
    setCustomerContactOptions(normalizedCustomerContacts);
    let contacts = [...normalizedCustomerContacts];

    const currentUserEmail = authState?.email || '';
    if (currentUserEmail && !contacts.some((contact) => contact.id === currentUserEmail)) {
      const label = authState?.name || currentUserEmail;
      contacts.unshift({ id: currentUserEmail, name: `${label} (You)` });
    }

    const additionalContacts = [...orderRepContacts, ...clientRepContacts].filter(Boolean);
    additionalContacts.forEach((contact) => {
      if (!contacts.some((existing) => existing.id === contact.id)) {
        contacts = [...contacts, contact];
      }
    });

    setAllContacts(contacts);

    // defaults
    const defaultToId =
      order?.clientContactId ||
      order?.presentation?.clientContactEmail ||
      order?.clientContactEmail ||
      order?.clientContact?.email ||
      '';

    // Prefill **TO** (not CC)
    let defaultTo = contacts.find(c => String(c.id) === String(defaultToId) || String(c.email || '').toLowerCase() === String(defaultToId || '').toLowerCase());
    if (!defaultTo && defaultToId) {
      defaultTo = { id: defaultToId, name: defaultToId };
      contacts = [...contacts, defaultTo];
    }
    setAllContacts(contacts);
    const selectedFromCustomerContacts = normalizedCustomerContacts.find(
      (c) =>
        String(c.id) === String(defaultToId) ||
        String(c.email || '').toLowerCase() === String(defaultToId || '').toLowerCase()
    );
    setSelectedClientContact(selectedFromCustomerContacts?.id || normalizedCustomerContacts?.[0]?.id || '');
    setEmailValue((prev) => ({
      ...prev,
      to: defaultTo ? [defaultTo] : [],
    }));

  }, [order, clientContacts, authState, orderRepContacts, clientRepContacts]);

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!instanceUniqueId) return;
      try {
        const headers = { headers: { accessToken: localStorage.getItem('accessToken') } };
        const instanceRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`,
          headers
        );
        const instanceId = instanceRes.data?.id;
        if (!instanceId) return;
        const templatesRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceId}/email-templates`,
          headers
        );
        const templates = templatesRes.data?.templates || [];
        setEmailTemplates(templates.filter((template) => template.enabled !== false));
      } catch (error) {
        console.warn('Failed to load email templates', error);
      }
    };

    fetchTemplates();
  }, [instanceUniqueId]);


  return (
    <>
      <Toaster position="top-right" />
      <Modal open={open} onClose={handleClose}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Typography className={styles.title} sx={{ mb: 2 }}>
            {popupTitle}
          </Typography>

          <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
            <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 1, p: 2, mb: 2 }}>
              {hasAttachedCustomerContacts ? (
                <ClientContactsDropdown
                  name="clientContact"
                  handleSelect={handleClientContactSelect}
                  selectedValue={selectedClientContact}
                  options={customerContactOptions}
                />
              ) : (
                <Typography variant="body2" color="error">
                  The following customer has no contacts attached, please add one to move forward.&nbsp;
                  {primaryCustomerId && (
                    <button
                      type="button"
                      onClick={openCustomerPopup}
                      style={{ background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                    >
                      Open customer
                    </button>
                  )}
                </Typography>
              )}
            </Box>

            {showSalesOrderBillingShipping && (
              <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 1, p: 2, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Sales Order Billing & Shipping</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Choose the billing/shipping contacts and addresses that will be saved on this Sales Order.
                  {enforceAddressBeforeSalesOrderSend && deliveryMode === 'send'
                    ? ' All four fields are required before Send Now.'
                    : ''}
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Box sx={{ display: 'grid', gap: 1.25 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Billing Contact"
                        value={billingContactId}
                        onChange={(event) => setBillingContactId(event.target.value)}
                        required={requireSalesOrderAddresses}
                      >
                        <MenuItem value="">Select billing contact</MenuItem>
                        {customerContactOptions.map((contact) => (
                          <MenuItem key={`billing-contact-${getContactValue(contact)}`} value={getContactValue(contact)}>
                            {contact.name || contact.email || getContactValue(contact)}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Tooltip title="Add billing contact" arrow>
                        <IconButton
                          size="small"
                          onClick={() => openAddContact('billing')}
                          sx={{
                            flex: '0 0 auto',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '7px',
                          }}
                        >
                          <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Billing Address"
                      value={billingAddressId}
                      onChange={(event) => setBillingAddressId(event.target.value)}
                      required={requireSalesOrderAddresses}
                    >
                      <MenuItem value="">Select billing address</MenuItem>
                      {customerAddresses.map((address) => (
                        <MenuItem key={`billing-${address.id}`} value={address.id}>
                          {address.label}{address.display ? ` — ${address.display}` : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      size="small"
                      onClick={() => openAddAddress('billing')}
                      sx={{ mt: 0.75, textTransform: 'none' }}
                    >
                      + Add billing address
                    </Button>
                  </Box>

                  <Box sx={{ display: 'grid', gap: 1.25 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Shipping Contact"
                        value={shippingContactId}
                        onChange={(event) => setShippingContactId(event.target.value)}
                        required={requireSalesOrderAddresses}
                      >
                        <MenuItem value="">Select shipping contact</MenuItem>
                        {customerContactOptions.map((contact) => (
                          <MenuItem key={`shipping-contact-${getContactValue(contact)}`} value={getContactValue(contact)}>
                            {contact.name || contact.email || getContactValue(contact)}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Tooltip title="Add shipping contact" arrow>
                        <IconButton
                          size="small"
                          onClick={() => openAddContact('shipping')}
                          sx={{
                            flex: '0 0 auto',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '7px',
                          }}
                        >
                          <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Shipping Address"
                      value={shippingAddressId}
                      onChange={(event) => setShippingAddressId(event.target.value)}
                      required={requireSalesOrderAddresses}
                    >
                      <MenuItem value="">Select shipping address</MenuItem>
                      {customerAddresses.map((address) => (
                        <MenuItem key={`shipping-${address.id}`} value={address.id}>
                          {address.label}{address.display ? ` — ${address.display}` : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      size="small"
                      onClick={() => openAddAddress('shipping')}
                      sx={{ mt: 0.75, textTransform: 'none' }}
                    >
                      + Add shipping address
                    </Button>
                  </Box>
                </Box>

                {requireSalesOrderAddresses && missingSalesOrderAddresses.length > 0 && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    Required before sending: {missingSalesOrderAddresses.join(' and ')}.
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 1, p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Delivery Option</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant={deliveryMode === 'draft' ? 'contained' : 'outlined'}
                  onClick={() => setDeliveryMode('draft')}
                >
                  Create Draft (no email)
                </Button>
                <Button
                  variant={deliveryMode === 'send' ? 'contained' : 'outlined'}
                  onClick={() => setDeliveryMode('send')}
                >
                  Send Now
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {deliveryMode === 'send'
                  ? 'Email fields are enabled and recipients are required.'
                  : 'This will create the document as a draft without sending an email.'}
              </Typography>
            </Box>

            {deliveryMode === 'send' && (
              <Box sx={{ mb: 2 }}>
                <EmailComposer
                  contacts={allContacts}
                  templates={emailTemplates}
                  value={emailValue}
                  onChange={setEmailValue}
                  attachments={emailAttachments}
                  onAttachmentsChange={setEmailAttachments}
                  quickActionRecipients={{
                    me: authState?.email ? [{ id: authState.email, name: `${authState?.name || authState.email} (You)` }] : [],
                    orderReps: orderRepContacts,
                    clientReps: clientRepContacts,
                  }}
                />
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <TableContainer component={Paper} className={`${styles.tableContainer} sticky-header`}>
            <Table className={styles.table}>
              <TableHead>
                <TableRow className={styles.tableRow}>
                  <TableCell className={styles.tableHeaderCell}>Select</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Quantity</TableCell>
                  <TableCell className={`${styles.tableHeaderCell} ${styles.buttonContainer} `}>Products</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Items</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Units</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Margin</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transformedData?.map((row, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Checkbox
                          checked={selectedProducts.includes(index)}
                          onChange={() => handleCheckboxChange(index)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box style={{ width: '100%' }}>
                          {row.quantity}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box className={styles.tableDetailContainer}>
                          <Box>
                            <img src={row.image} alt={row.name} className={styles.productImage} />
                          </Box>
                          <Box>
                            <Typography variant='body2' color={'#179ea5'}>{row.id}</Typography>
                            <Typography variant='body2' color={'#179ea5'}>{row.supplier}</Typography>
                            <Typography>{row.name}</Typography>
                            {(productFeedback[String(row.id ?? row.productId)] || []).map((feedback) => {
                              const clientName = customerContactOptions.find((contact) =>
                                String(contact?.contactId || contact?.id || '') === String(selectedClientContact)
                              )?.name || 'Client';
                              const initials = clientName.trim().split(/\s+/).slice(0, 2).map((word) => word[0]?.toUpperCase()).join('') || 'C';
                              return (
                                <Box key={feedback.commentId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1, maxWidth: 360 }}>
                                  <Box sx={{ flexShrink: 0, width: 27, height: 27, borderRadius: '50%', bgcolor: '#f5c6ca', color: '#8d2535', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700 }}>{initials}</Box>
                                  <Box sx={{ bgcolor: '#fff0f1', color: '#752a36', px: 1.5, py: 1, borderRadius: '12px', border: '1px solid #f4d3d7', minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{clientName}</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{feedback.comment}</Typography>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell colSpan={4}>
                        <Box className={styles.scrollableChildRows}>
                          <Box className={styles.pricingGrid}>
                            <Box className={styles.pricingColumn} sx={{ width: '80px' }}>
                              {extractKey(row?.data, 'item')?.map((data, i) => (
                                <Box key={i} display="flex" flexDirection="column" alignItems="flex-start">
                                  <Typography sx={{ fontWeight: data?.item === 'Total' ? 700 : 'normal', whiteSpace: 'nowrap' }}>
                                    {data.item}
                                  </Typography>
                                  {data?.item === 'Total' && row.isTaxPresent && (
                                    <Typography variant="body2" sx={{ fontSize: '0.85em', color: '#555', mt: '2px', ml: '2px' }}>
                                      (incl. of taxes)
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                            <Box className={styles.pricingColumn}>
                              {extractKey(row?.data, UNIT)?.map((data, i) => (
                                <Typography key={i} sx={{ display: 'flex', justifyContent: 'center' }}>{data[UNIT]}</Typography>
                              ))}
                            </Box>
                            <Box className={styles.pricingColumn}>
                              {extractKey(row?.data, MARGIN)?.map((data, i) => (
                                <Typography key={i} sx={{ display: 'flex', justifyContent: 'center' }}>{data[MARGIN]}%</Typography>
                              ))}
                            </Box>
                            <Box className={styles.pricingColumn}>
                              {extractKey(row?.data, AMOUNT)?.map((data, i) => (
                                <Typography key={i} sx={{ display: 'flex', justifyContent: 'center' }}>
                                  ${Number(data[AMOUNT] || 0).toFixed(2)}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            </TableContainer>
          </Box>

          <Box sx={{ px: 1, py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox size="small" checked={deleteExcludedProducts} onChange={(event) => setDeleteExcludedProducts(event.target.checked)} disabled={loading} inputProps={{ 'aria-label': 'Delete excluded products from Order Cart' }} />
              <Typography variant="body2" fontWeight={600}>Delete excluded products from Order Cart</Typography>
            </Box>
            <Typography variant="caption" color="error.main" sx={{ display: 'block', ml: 5 }}>
              Warning: Unchecked products will be permanently deleted from the cart after the document is created. Their pricing, decorations, and artwork cannot be restored. Protected items may not be deleted.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button
              variant="contained"
              color="primary"
              className={styles.cancelButton}
              onClick={handleClose}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="primary"
              className={styles.estimateButton}
              onClick={handleConvertToEstimate}
              disabled={loading || isCreateBlocked}
            >
              {loading ? <ButtonLoader /> : ctaText}
            </Button>
          </Box>
        </Box>
      </Modal>

      <CustomerContactDialog
        open={contactDialog.open}
        loading={contactDialog.loading}
        onClose={closeAddContact}
        onSubmit={handleContactCreated}
        targetLabel={contactDialog.target === 'shipping' ? 'Shipping Contact' : 'Billing Contact'}
      />

      <AddCustomerAddressModal
        open={addAddressOpen}
        onClose={() => {
          setAddAddressOpen(false);
          setAddressTarget(null);
        }}
        customerId={primaryCustomerId}
        onSaved={handleAddressSaved}
      />
    </>
  );
};

export default SelectedProductsPopup;
